import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Alert,
  message,
} from "antd";
import { handleApiSuccess, handleApiError } from "utils";
import {
  billingRecurringByIdService,
  billingRecurringCreateService,
  billingRecurringUpdateService,
  billingItemListService,
  billingCustomerListService,
  billingSalesAgentListService,
} from "services/billing";
import LineItems from "../invoices/_lineItems";
import { FREQUENCIES } from "../constants";

const { Option } = Select;

export default function RecurringForm({ id }) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);

  const frequency = Form.useWatch("frequency", form);

  useEffect(() => {
    Promise.all([
      billingItemListService(),
      billingCustomerListService(),
      billingSalesAgentListService(),
    ])
      .then(([itemsRes, custRes, agentsRes]) => {
        setCatalog(itemsRes.data.items || []);
        setCustomers(custRes.data.items || []);
        setAgents(agentsRes.data.items || []);
      })
      .catch((error) => handleApiError(error));

    if (id) {
      billingRecurringByIdService(id)
        .then((response) => {
          const t = response.data.item;
          if (!t) return;
          setItems((t.items || []).map((l, i) => ({ ...l, key: l._id || i })));
          form.setFieldsValue({
            ...t,
            company: t.company && t.company._id ? t.company._id : t.company,
            customer: t.customer && t.customer._id ? t.customer._id : t.customer,
            sales_agent:
              t.sales_agent && t.sales_agent._id ? t.sales_agent._id : t.sales_agent,
            start_date: t.start_date ? moment(t.start_date) : null,
            end_date: t.end_date ? moment(t.end_date) : null,
            next_service_month: t.next_service_month ? moment(t.next_service_month) : null,
            tags: t.tags || [],
          });
        })
        .catch((error) => handleApiError(error));
    } else {
      form.setFieldsValue({
        frequency: "MONTHLY",
        interval: 1,
        due_days: 15,
        discount_type: "FIXED",
        discount_value: 0,
        adjustment: 0,
        currency: "USD",
        status: "ACTIVE",
        auto_send: false,
        start_date: moment(),
        next_service_month: moment(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onFinish = (values) => {
    if (items.length === 0) {
      message.error("Add at least one line item");
      return;
    }
    setIsLoading(true);
    const asDate = (value) =>
      value && value.isValid && value.isValid() ? value.format("YYYY-MM-DD") : null;
    const startDate = asDate(values.start_date);
    if (!startDate) {
      setIsLoading(false);
      message.error("Start date is required");
      return;
    }
    if (values.end_date && !asDate(values.end_date)) {
      setIsLoading(false);
      message.error("End date is not a valid date");
      return;
    }
    if (values.next_service_month && !asDate(values.next_service_month)) {
      setIsLoading(false);
      message.error("Service month is not a valid date");
      return;
    }
    const payload = {
      ...values,
      start_date: startDate,
      end_date: asDate(values.end_date),
      next_service_month: values.next_service_month
        ? values.next_service_month.clone().startOf("month").format("YYYY-MM-DD")
        : startDate,
      items: items.map((l) => ({
        product_id: l.product_id || null,
        product_type: l.product_type,
        product_name: l.product_name,
        description: l.description,
        unit: l.unit,
        quantity: Number(l.quantity) || 0,
        rate: Number(l.rate) || 0,
        discount_percentage: Number(l.discount_percentage) || 0,
        is_taxable: l.is_taxable !== false,
        tax_rate_id: l.tax_rate_id || null,
      })),
    };
    const action = id
      ? billingRecurringUpdateService(id, payload)
      : billingRecurringCreateService(payload);
    action
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        history.push("/billing/recurring");
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Template Details" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Template Title" name="title">
                  <Input placeholder="e.g. Monthly Hosting - Acme" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Customer"
                  name="company"
                  rules={[{ required: true, message: "Select a customer" }]}
                >
                  <Select showSearch optionFilterProp="children" placeholder="Select customer">
                    {customers.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.company_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Sales Agent" name="sales_agent">
                  <Select showSearch allowClear optionFilterProp="children" placeholder="Select agent">
                    {agents.map((a) => (
                      <Option key={a._id} value={a._id}>
                        {a.display_name ||
                          `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
                          a.email}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Line Items" size="small" style={{ marginBottom: 16 }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="Use variables in descriptions"
              description="You can use {{service_month}}, {{prev_month}}, {{next_month}} or {{year}} in a line description. They are resolved automatically each time an invoice is generated."
            />
            <LineItems items={items} onChange={setItems} catalog={catalog} currency="USD" />
          </Card>

          <Card title="Notes & Terms" size="small">
            <Form.Item label="Client Note" name="client_note">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item label="Admin Note (private)" name="admin_note">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item label="Terms & Conditions" name="terms_conditions">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Schedule" size="small" style={{ marginBottom: 16 }}>
            <Form.Item label="Frequency" name="frequency">
              <Select>
                {FREQUENCIES.map((f) => (
                  <Option key={f.value} value={f.value}>
                    {f.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {frequency === "EVERY_X_MONTHS" && (
              <Form.Item label="Every X Months" name="interval">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            )}
            <Form.Item
              label="Start Date"
              name="start_date"
              rules={[{ required: true, message: "Start date required" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="End Date" name="end_date" help="Leave empty for no end">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Service Month (start)"
              name="next_service_month"
              help="Auto-increments each cycle"
            >
              <DatePicker picker="month" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Due in (days)" name="due_days">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Status" name="status">
              <Select>
                <Option value="ACTIVE">Active</Option>
                <Option value="PAUSED">Paused</Option>
                <Option value="ENDED">Ended</Option>
              </Select>
            </Form.Item>
            <Form.Item name="auto_send" valuePropName="checked">
              <Checkbox>Automatically email generated invoices (Save &amp; Send)</Checkbox>
            </Form.Item>
            <Form.Item name="prevent_overdue_reminders" valuePropName="checked">
              <Checkbox>Do not send overdue reminders</Checkbox>
            </Form.Item>
          </Card>

          <Card title="Totals & Tags" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Discount Type" name="discount_type">
                  <Select>
                    <Option value="FIXED">Fixed $</Option>
                    <Option value="PERCENT">Percent %</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Discount" name="discount_value">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Adjustment (+/-)" name="adjustment">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Currency" name="currency">
              <Input />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select mode="tags" placeholder="Type and press Enter" />
            </Form.Item>
          </Card>

          <Button type="primary" htmlType="submit" loading={isLoading} block>
            {id ? "Update Template" : "Create Template"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
}
